'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const pathname = usePathname();

  const navigationItems = [
    { label: 'Home', href: '/homepage', icon: 'HomeIcon' },
    { label: 'Stories Library', href: '/stories-library', icon: 'BookOpenIcon' },
    { label: 'Download App', href: '/app-download', icon: 'ArrowDownTrayIcon' },
    { label: 'Creator Community', href: '/creator-community', icon: 'UserGroupIcon' },
  ];

  const moreMenuItems = [
    { label: 'About Us', href: '/about-us', icon: 'InformationCircleIcon' },
    { label: 'Regional Hubs', href: '/regional-hubs', icon: 'GlobeAltIcon' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  const isActive = (href: string) => pathname === href;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-card shadow-warm' : 'bg-card'
        } ${className}`}
      >
        <div className="w-full">
          <div className="flex items-center justify-between h-16 px-4 md:px-8">
            <Link href="/homepage" className="flex items-center space-x-3 group">
              <div className="relative w-12 h-12 flex-shrink-0 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                <AppImage
                  src="/assets/images/image-1767859099901.png"
                  alt="StoryNest-FM Logo"
                  width={48}
                  height={48}
                  className="w-12 h-12 object-contain"
                />
              </div>
              <span className="text-lg md:text-xl font-heading font-bold text-primary hidden sm:block">
                StoryNest-FM
              </span>
            </Link>

            <nav className="hidden lg:flex items-center space-x-1">
              {navigationItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                    isActive(item.href)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-text-primary hover:bg-muted hover:text-primary'
                  }`}
                >
                  <Icon name={item.icon as any} size={20} />
                  <span>{item.label}</span>
                </Link>
              ))}

              <div className="relative group">
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-text-primary hover:bg-muted hover:text-primary transition-all duration-300">
                  <Icon name="EllipsisHorizontalIcon" size={20} />
                  <span>More</span>
                </button>

                <div className="absolute right-0 top-full mt-2 w-56 bg-popover rounded-lg shadow-warm-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                  <div className="py-2">
                    {moreMenuItems.map((item) => (
                      <Link
                        key={item.href}
                        href={item.href}
                        className={`flex items-center space-x-3 px-4 py-3 transition-colors duration-200 ${
                          isActive(item.href)
                            ? 'bg-primary text-primary-foreground'
                            : 'text-text-primary hover:bg-muted hover:text-primary'
                        }`}
                      >
                        <Icon name={item.icon as any} size={20} />
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </nav>

            <div className="flex items-center space-x-4">
              <Link
                href="/app-download"
                className="hidden md:flex items-center space-x-2 px-6 py-2.5 bg-cta text-cta-foreground rounded-lg font-cta font-bold transition-all duration-300 hover:bg-cta/90 hover:shadow-warm"
              >
                <Icon name="ArrowDownTrayIcon" size={20} />
                <span>Get App</span>
              </Link>

              <button
                onClick={toggleMobileMenu}
                className="lg:hidden p-2 rounded-lg text-text-primary hover:bg-muted transition-colors duration-200"
                aria-label="Toggle mobile menu"
              >
                <Icon
                  name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
                  size={24}
                />
              </button>
            </div>
          </div>
        </div>
      </header>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-background"
            onClick={toggleMobileMenu}
          />

          <div className="absolute top-16 left-0 right-0 bottom-0 bg-card overflow-y-auto">
            <nav className="py-4">
              <div className="space-y-1 px-4">
                {navigationItems.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={toggleMobileMenu}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                      isActive(item.href)
                        ? 'bg-primary text-primary-foreground'
                        : 'text-text-primary hover:bg-muted hover:text-primary'
                    }`}
                  >
                    <Icon name={item.icon as any} size={22} />
                    <span>{item.label}</span>
                  </Link>
                ))}

                <div className="pt-4 mt-4 border-t border-border">
                  <p className="px-4 py-2 text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    More
                  </p>
                  {moreMenuItems.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={toggleMobileMenu}
                      className={`flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                        isActive(item.href)
                          ? 'bg-primary text-primary-foreground'
                          : 'text-text-primary hover:bg-muted hover:text-primary'
                      }`}
                    >
                      <Icon name={item.icon as any} size={22} />
                      <span>{item.label}</span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="px-4 mt-6">
                <Link
                  href="/app-download"
                  onClick={toggleMobileMenu}
                  className="flex items-center justify-center space-x-2 w-full px-6 py-3 bg-cta text-cta-foreground rounded-lg font-cta font-bold transition-all duration-300 hover:bg-cta/90 hover:shadow-warm"
                >
                  <Icon name="ArrowDownTrayIcon" size={20} />
                  <span>Get App</span>
                </Link>
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;